import React, { useState, useEffect, useRef } from 'react';
import { Input, Tooltip } from 'antd';
import FullScreenButton from '@components/fullScreenButton';
import { PowerBI } from './powerbiEmbed';
import { API } from '@service';
import { INTEGRATION_TYPES } from '@model/projectDetails';
import { Loading } from '@components';
import { Button } from 'appkit-react';
import { addHTTP } from '@utils/common';
import SpnId from '@components/SpnID/SpnId';
import useProjectId from '@hooks/useProjectId';
import { FixedRatioSectionWrapper } from '@components/fixedRatioContainer';
import SharePointIframe, {
  validateM365URL,
} from '@components/sharePointDisplay/SharePointIframe';
import { filterXSS } from 'xss';

const isOtherType = (type) =>
  [INTEGRATION_TYPES.TEXT, INTEGRATION_TYPES.OTHER].includes(type);

const TYPE_PLACEHOLDER_MAP = {
  [INTEGRATION_TYPES.POWER_BI]: 'a PowerBI Dashboard',
  [INTEGRATION_TYPES.PDF]: 'a pdf',
  [INTEGRATION_TYPES.OTHER]: 'an External',
  [INTEGRATION_TYPES.TEXT]: 'an External',
  [INTEGRATION_TYPES.M365]: 'an M365',
};

const IntegrationDisplay = ({
  type = INTEGRATION_TYPES.POWER_BI,
  link,
  propsLink,
  saveLink,
  section,
  setLink,
  identifier,
  handleUserGuid,
  titleName,
  hideEdit,
  sectionOrder,
  ...rest
}) => {
  const [pass, setPass] = useState(false);
  const [powerbiData, setPowerbiData] = useState('');
  const [value, setV] = useState(propsLink);
  const [isLoading, toggleLoading] = useState(true);
  const [error, toggleError] = useState(false);
  // const [timer, setTimer] = useState(null);
  const projectId = useProjectId();

  const [refeshToken, setRefeshToken] = useState(null);
  const pbiDataRef = useRef(refeshToken);
  const timerRef = useRef();

  useEffect(
    () => () => {
      console.log(`clearTimer ${timerRef.current}`);
      clearInterval(timerRef.current);
    },
    [],
  );

  const runTimer = (link) => {
    // console.log(`runTimer ${link}`);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    const timerID = setInterval(async () => {
      console.log(`timer run moduleId:${identifier}`);
      const result = await API.postIntegrationHubModule(
        {
          integrationHubModuleInfo: [{ moduleId: identifier, link }],
        },
        projectId,
      );
      if (Array.isArray(result) && result[0].reportInfo?.status === 200) {
        if (result[0].reportInfo.accessToken !== refeshToken) {
          console.log(result[0].reportInfo);
          console.log(refeshToken);
          setRefeshToken(result[0].reportInfo.accessToken);
          pbiDataRef.current = result[0].reportInfo;
        }
      }
    }, 8 * 60 * 1000);

    console.log(timerID);
    timerRef.current = timerID;
  };

  const fetchData = async (link) => {
    const result = await API.postIntegrationHubModule(
      {
        integrationHubModuleInfo: [{ moduleId: identifier, link }],
      },
      projectId,
    );

    if (Array.isArray(result) && result[0].reportInfo?.status === 200) {
      setPowerbiData(result[0].reportInfo);
      pbiDataRef.current = result[0].reportInfo;
      setRefeshToken(result[0].reportInfo.accessToken);
      runTimer(value);
      return { status: 200, info: result[0].reportInfo };
    }
    return { status: 418 };
  };

  const handleInsertLink = async (v, shouldSave = true) => {
    const url = typeof v === 'string' ? v : value;
    toggleLoading(true);
    setV(url);

    if (isOtherType(type) || type === INTEGRATION_TYPES.M365) {
      saveLink && shouldSave && saveLink(section, url);
      setLink(url);
      toggleLoading(false);
      return true;
    }

    let res = { status: 200 };

    if (url !== '') {
      res = await fetchData(url);
    }

    if (res?.status === 200) {
      saveLink && shouldSave && saveLink(section, url);
      setLink(url);
      toggleLoading(false);
      return true;
    }

    toggleLoading(false);
    toggleError(true);
    return false;
  };

  useEffect(() => {
    if (typeof propsLink === 'string') {
      const linkValid = verifyUrl(propsLink, type);
      if (linkValid) {
        handleInsertLink(propsLink, false);
      } else {
        setLink('');
      }
      setV(propsLink);
    }
  }, [propsLink]);

  const verifyUrl = (value, type) => {
    // // const u = `https://api.powerbi.com?${value}`;
    // const u = `https://app.powerbi.com/groups/50d4a1db-8a38-417c-9940-6d08379dc295/reports/a4bed50a-13c6-407c-9b52-aefa540a8500/ReportSection`;
    if (isOtherType(type)) {
      setPass(true);
      return true;
    }

    if (type === INTEGRATION_TYPES.M365) {
      const valid = validateM365URL(value);
      setPass(valid);
      return valid;
    }

    try {
      const url = new URL(value);
      const pathName = url.pathname.split('/') || [];
      const spaceId = pathName[2];
      const reportId = pathName[4];
      if (
        spaceId &&
        reportId &&
        pathName[1] === 'groups' &&
        pathName[3] === 'reports'
      ) {
        toggleError(false);
        setPass(true);
        return true;
      }
      setPass(false);
      toggleLoading(false);
      return false;
    } catch {
      setPass(false);
      toggleLoading(false);
      return false;
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    verifyUrl(v, type);
    setV(v);
  };

  if (!link && !isLoading) {
    return (
      <>
        <span className="powerbi-title">{titleName}</span>
        <div className="link-input-container">
          {type === INTEGRATION_TYPES.POWER_BI ? (
            <>
              <SpnId classname="role-control" />
              <div className="link-text role-control">Link</div>
            </>
          ) : null}
          <div className="insert-link-input role-control">
            <Tooltip
              title={
                type === INTEGRATION_TYPES.POWER_BI
                  ? 'https://app.powerbi.com/groups/???/reports/???/ReportSection'
                  : null
              }
              placement="topLeft"
              overlayClassName="edit-hub-tooltip"
            >
              <Input
                placeholder={`Insert ${
                  TYPE_PLACEHOLDER_MAP[type || INTEGRATION_TYPES.POWER_BI]
                } URL`}
                onChange={handleChange}
                value={value}
              />
            </Tooltip>
            <Button
              isLoading={isLoading}
              size="lg"
              onClick={() => {
                handleInsertLink();
              }}
              disabled={!pass}
              // style={{ marginLeft: '1.25rem' }}
            >
              insert link
            </Button>
          </div>
          {error && (
            <div className="hub-error">
              <div>
                Error Message: The workspace is not authorized to be shown in
                M&A Gateway.
              </div>
              <div>
                Please grant permission and try again. For steps on how to grant
                permission, leverage the User Guide.
                {/*<LinkButton*/}
                {/*  type="link"*/}
                {/*  onClick={handleUserGuid}*/}
                {/*  style={{ padding: 0 }}*/}
                {/*>*/}
                {/*  User Guide.*/}
                {/*</LinkButton>*/}
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="powerbi-title-link">
        <div className="left">
          <span className="title">{titleName}</span>
          {/* <span className="appkiticon icon-pencil-fill" /> */}
          {/* {!hideEdit && (
            <EditModal
              handleUserGuid={handleUserGuid}
              section={section}
              handleSaveLink={handleSaveLink}
              sectionOrder={sectionOrder}
              link={link}
              setLink={setLink}
              type={type}
              clearLink={setV}
              setDisplayLoading={toggleLoading}
              fetchData={fetchData}
              {...rest}
            />
          )} */}
        </div>
        {!hideEdit && (
          <div className="right">
            <FullScreenButton
              elemId={`#fullscreen-${identifier}`}
              scrollIntoViewAfterExit={type === INTEGRATION_TYPES.M365}
            />
          </div>
        )}
      </div>
      <FixedRatioSectionWrapper
        ratio={section.ratio}
        className="powerbi-fullscreen-div"
        id={`fullscreen-${identifier}`}
      >
        {type === INTEGRATION_TYPES.M365 ? (
          <SharePointIframe link={link} />
        ) : isOtherType(type) ? (
          <iframe
            title="inte-iframe"
            className="external-iframe"
            src={filterXSS(addHTTP(link, 'https://'))}
          />
        ) : !powerbiData ? (
          <Loading />
        ) : (
          <PowerBI
            data={powerbiData}
            {...rest}
            token={refeshToken ? refeshToken : powerbiData?.accessToken}
          />
        )}
      </FixedRatioSectionWrapper>
    </>
  );
};

export default IntegrationDisplay;
